import React, { useEffect, useState, useCallback } from 'react';
import {
  analyticsOverview, analyticsTopQuestions, analyticsGaps,
  analyticsProducts, analyticsTiming, analyticsFeedback,
  analyticsUsage, analyticsChips, analyticsExportCsvUrl,
  downloadAnalyticsXlsx,
} from '../../api/client';

/* ─── Line icons (stroke=currentColor, no fill) ─────────────────────────
   Style: 24×24 viewBox, stroke-width 2, rounded line caps/joins — matches
   the outlined external-link reference. Each Icon scales via the `size`
   prop and inherits the parent's color. */
function Icon({ name, size = 18, style }) {
  const props = {
    width: size, height: size, viewBox: '0 0 24 24',
    fill: 'none', stroke: 'currentColor', strokeWidth: 2,
    strokeLinecap: 'round', strokeLinejoin: 'round',
    style: { flexShrink: 0, ...style },
  };
  switch (name) {
    case 'chat':       return <svg {...props}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
    case 'users':      return <svg {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case 'thumbsUp':   return <svg {...props}><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" /></svg>;
    case 'bolt':       return <svg {...props}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
    case 'rocket':     return <svg {...props}><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /></svg>;
    case 'alert':      return <svg {...props}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
    case 'target':     return <svg {...props}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>;
    case 'building':   return <svg {...props}><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" /></svg>;
    case 'globe':      return <svg {...props}><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>;
    case 'refresh':    return <svg {...props}><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>;
    case 'sheet':      return <svg {...props}><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" /></svg>;
    case 'thumbsDown': return <svg {...props}><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zM17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" /></svg>;
    case 'clock':      return <svg {...props}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
    case 'calendar':   return <svg {...props}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
    default:           return null;
  }
}

// One-time keyframe injection for the refresh spinner.
if (typeof document !== 'undefined' && !document.getElementById('at-keyframes')) {
  const el = document.createElement('style');
  el.id = 'at-keyframes';
  el.textContent = '@keyframes atSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
  document.head.appendChild(el);
}

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: '7d',    label: '7 days' },
  { key: '30d',   label: '30 days' },
  { key: '90d',   label: '90 days' },
  { key: 'all',   label: 'All time' },
];

const fmtMs = (ms) => {
  if (!ms) return '—';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
};

const fmtPct = (n) => `${Math.round((n || 0) * 100)}%`;

const card = {
  background: 'linear-gradient(135deg, #ffffff 0%, #f0fafa 60%, #e6f7f7 100%)',
  border: '1px solid rgba(29, 156, 156, 0.18)',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 4px 20px rgba(13, 79, 82, 0.05), 0 1px 2px rgba(0,0,0,0.04)',
  position: 'relative',
  overflow: 'hidden',
};

const accent = {
  position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
  background: 'linear-gradient(90deg, #0d4f52, #1d9c9c, #4fd1c5)',
};

const sectionTitle = { fontSize: '15px', fontWeight: 800, color: '#0f172a', marginBottom: '14px', letterSpacing: '-0.01em' };

export default function AnalyticsTab() {
  const [range, setRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [top, setTop] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [products, setProducts] = useState([]);
  const [timing, setTiming] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [usage, setUsage] = useState(null);
  const [chips, setChips] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, tq, gp, pr, tm, fb, us, ch] = await Promise.all([
        analyticsOverview(range),
        analyticsTopQuestions(range, 10),
        analyticsGaps(range, 10),
        analyticsProducts(range),
        analyticsTiming(range),
        analyticsFeedback(range),
        analyticsUsage(range),
        analyticsChips(range),
      ]);
      setOverview(ov.data);
      setTop(tq.data.items || []);
      setGaps(gp.data.items || []);
      setProducts(pr.data.items || []);
      setTiming(tm.data);
      setFeedback(fb.data);
      setUsage(us.data);
      setChips(ch.data);
    } catch (e) {
      // best-effort — partial UI is fine
    }
    setLoading(false);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Range selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Time range:
        </span>
        {RANGES.map((r) => {
          const active = range === r.key;
          return (
            <span
              key={r.key}
              onClick={() => setRange(r.key)}
              style={{
                padding: '7px 16px',
                borderRadius: '999px',
                fontSize: '12.5px',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: active ? 700 : 500,
                background: active ? 'linear-gradient(135deg, #1d9c9c, #0d7377)' : 'rgba(255,255,255,0.85)',
                color: active ? '#fff' : '#475569',
                border: '1px solid ' + (active ? 'transparent' : 'rgba(29,156,156,0.25)'),
                boxShadow: active ? '0 4px 12px rgba(13,115,119,0.25)' : '0 1px 2px rgba(0,0,0,0.03)',
                transition: 'all 0.18s',
              }}
            >{r.label}</span>
          );
        })}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button
            onClick={() => downloadAnalyticsXlsx(range).catch(() => {})}
            style={{
              padding: '9px 18px', borderRadius: 'var(--radius-btn)', fontSize: '12.5px', fontWeight: 700,
              border: 'none', cursor: 'pointer', color: '#fff',
              background: 'var(--teal)',
              boxShadow: '0 2px 6px rgba(13,115,119,0.18)',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Icon name="sheet" size={14} /> Export Excel
            </span>
          </button>
          <button
            onClick={load}
            disabled={loading}
            style={{
              padding: '9px 18px', borderRadius: 'var(--radius-btn)', fontSize: '12.5px', fontWeight: 700,
              border: 'none', cursor: loading ? 'wait' : 'pointer', color: '#fff',
              background: 'var(--teal-dark)',
              boxShadow: '0 2px 6px rgba(13,115,119,0.18)',
              opacity: loading ? 0.85 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              transition: 'opacity 0.15s',
            }}
          >
            {loading ? (
              <>
                <span style={{
                  width: 12, height: 12, borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.35)',
                  borderTopColor: '#fff',
                  animation: 'atSpin 0.8s linear infinite',
                  display: 'inline-block',
                }} />
                Refreshing…
              </>
            ) : (<><Icon name="refresh" size={14} /> Refresh</>)}
          </button>
        </span>
      </div>

      {loading && !overview && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading…</div>
      )}

      {/* KPI cards */}
      {overview && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px' }}>
          <Kpi label="Total Queries"  value={overview.total_queries}     icon="chat" />
          <Kpi label="Unique Users"   value={overview.unique_users}      icon="users" />
          <Kpi
            label="Helpful Rate"
            value={fmtPct(overview.helpful_rate)}
            icon="thumbsUp"
            sub={
              <span style={{ fontWeight: 700, color: '#0f172a' }}>
                <span style={{ color: '#16a34a' }}>{overview.feedback_up}</span> up
                <span style={{ color: '#94a3b8', fontWeight: 500 }}> · </span>
                <span style={{ color: '#dc2626' }}>{overview.feedback_down}</span> down
              </span>
            }
          />
          <Kpi label="Avg Response"   value={fmtMs(overview.avg_response_ms)} icon="bolt" />
          <Kpi label="Cache Hit Rate" value={fmtPct(overview.cache_hit_rate)} icon="rocket" />
          <Kpi label="No-Answer Rate" value={fmtPct(overview.no_answer_rate)} icon="alert" sub="opportunities" />
        </div>
      )}

      {/* Query volume sparkline */}
      {overview?.series?.length > 0 && (
        <div style={card}>
          <div style={accent} />
          <div style={sectionTitle}>Query Volume</div>
          <Sparkbars series={overview.series} />
        </div>
      )}

      {/* Two-column row: Top Questions + Knowledge Gaps */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px' }}>
        <div style={card}>
          <div style={accent} />
          <div style={sectionTitle}>Top 10 Questions</div>
          {top.length === 0
            ? <Empty text="No questions tracked yet." />
            : <ListRows
                items={top}
                render={(r) => (
                  <>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.question}</span>
                    <span style={pill}>{r.count}×</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>{fmtMs(r.avg_ms)}</span>
                  </>
                )}
              />
          }
        </div>

        <div style={card}>
          <div style={accent} />
          <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#c53030' }}><Icon name="alert" size={18} /></span>
            Knowledge Gaps
            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>(no good answer found)</span>
          </div>
          {gaps.length === 0
            ? <Empty text="No knowledge gaps detected — nice!" />
            : <ListRows
                items={gaps}
                render={(r) => (
                  <>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.question}</span>
                    <span style={{ ...pill, background: 'rgba(252,129,129,0.15)', color: '#c53030' }}>{r.count}×</span>
                  </>
                )}
              />
          }
        </div>
      </div>

      {/* Two-column row: Products + Timing */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px' }}>
        <div style={card}>
          <div style={accent} />
          <div style={sectionTitle}>Queries by Product</div>
          {products.length === 0
            ? <Empty text="No product activity yet." />
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {products.map((p) => (
                  <div key={p.product}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{p.product}</span>
                      <span style={{ color: '#64748b' }}>{p.count} · {p.percent}%</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 4, background: 'rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${p.percent}%`,
                        background: 'linear-gradient(90deg, #1d9c9c, #0d7377)',
                        transition: 'width 0.5s',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>

        <div style={card}>
          <div style={accent} />
          <div style={sectionTitle}>Response Timing</div>
          {!timing || timing.count === 0
            ? <Empty text="No timing data yet." />
            : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <TimingTile label="p50 (median)" value={fmtMs(timing.p50)} />
                <TimingTile label="p95"          value={fmtMs(timing.p95)} />
                <TimingTile label="Slowest"      value={fmtMs(timing.max)} />
              </div>
          }
          {timing?.count > 0 && (
            <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
              Across <b>{timing.count}</b> answered queries.
            </div>
          )}
        </div>
      </div>

      {/* Internal vs Customer usage split */}
      {usage && (
        <div style={card}>
          <div style={accent} />
          <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#0d7377' }}><Icon name="users" size={18} /></span>
            Internal vs Customer Usage
          </div>
          {usage.total === 0 ? (
            <Empty text="No queries in this range yet." />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
              {usage.items.map((row) => {
                const isInternal = row.user_type === 'internal';
                return (
                  <div key={row.user_type} style={{
                    padding: '14px 16px', borderRadius: 12,
                    background: isInternal
                      ? 'linear-gradient(135deg, rgba(29,156,156,0.10), rgba(13,115,119,0.06))'
                      : 'linear-gradient(135deg, rgba(99,102,241,0.10), rgba(79,70,229,0.06))',
                    border: '1px solid ' + (isInternal ? 'rgba(29,156,156,0.30)' : 'rgba(99,102,241,0.30)'),
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ color: isInternal ? '#1d9c9c' : '#6366f1', display: 'inline-flex' }}>
                        <Icon name={isInternal ? 'building' : 'globe'} size={20} />
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {isInternal ? 'Internal (DeliverHealth)' : 'Customer'}
                      </span>
                      <span style={{ marginLeft: 'auto', padding: '2px 10px', borderRadius: 999, background: isInternal ? '#1d9c9c' : '#6366f1', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                        {row.percent}%
                      </span>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{row.count}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>queries</div>
                    <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 11.5, color: '#475569' }}>
                      <span><b>{row.unique_users}</b> users</span>
                      <span>{fmtMs(row.avg_response_ms)} avg</span>
                      <span><b>{row.no_answer}</b> misses</span>
                    </div>
                    <div style={{ marginTop: 10, height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${row.percent}%`,
                        background: isInternal
                          ? 'linear-gradient(90deg, #1d9c9c, #0d7377)'
                          : 'linear-gradient(90deg, #6366f1, #4f46e5)',
                        transition: 'width 0.5s',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 10 }}>
            Internal = loopback / RFC1918 / link-local. Customer = everything else.
          </div>
        </div>
      )}

      {/* Most Clicked Suggestion Chips */}
      {chips && (
        <div style={card}>
          <div style={accent} />
          <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#0d7377' }}><Icon name="target" size={18} /></span>
            Most Clicked Options
            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>(welcome-screen chip taps)</span>
          </div>
          {chips.total === 0 ? (
            <Empty text="No chip clicks yet — once users tap product/role chips, counts appear here." />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
              {Object.entries(chips.by_group).map(([groupName, rows]) => (
                rows.length === 0 ? null : (
                  <div key={groupName} style={{
                    padding: '14px 16px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.85)',
                    border: '1px solid rgba(29,156,156,0.15)',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#0d7377', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                      {groupName}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {rows.map((r) => {
                        const maxCount = rows[0].count || 1;
                        const barPct = Math.round((r.count / maxCount) * 100);
                        return (
                          <div key={r.label}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 3 }}>
                              <span style={{ fontWeight: 600 }}>{r.label}</span>
                              <span style={{ color: '#64748b' }}><b>{r.count}</b> · {r.percent}%</span>
                            </div>
                            <div style={{ height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                              <div style={{
                                height: '100%', width: `${barPct}%`,
                                background: 'linear-gradient(90deg, #1d9c9c, #0d7377)',
                                transition: 'width 0.4s',
                              }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 10 }}>
            Total chip clicks tracked: <b>{chips.total}</b>
          </div>
        </div>
      )}

      {/* Negative feedback list */}
      {feedback?.recent_down?.length > 0 && (
        <div style={card}>
          <div style={accent} />
          <div style={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#c53030' }}><Icon name="thumbsDown" size={18} /></span>
            Recent Negative Feedback
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {feedback.recent_down.map((f, i) => (
              <div key={i} style={{
                padding: '10px 14px', borderRadius: 10,
                background: 'rgba(252,129,129,0.06)',
                border: '1px solid rgba(252,129,129,0.18)',
                fontSize: 13, color: '#0f172a',
              }}>
                <div style={{ fontWeight: 600 }}>{f.question || '(no question text)'}</div>
                {f.comment && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>"{f.comment}"</div>}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{f.created_at}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── tiny presentational pieces ───────────────────────────────────────── */

const pill = {
  padding: '2px 10px',
  borderRadius: 999,
  background: 'var(--accent-light, #e6f7f7)',
  color: '#0d7377',
  fontWeight: 700,
  fontSize: 11.5,
};

function Kpi({ label, value, icon, sub }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.85)',
      border: '1px solid rgba(29,156,156,0.15)',
      borderRadius: '12px',
      padding: '16px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ marginBottom: 6, color: '#0d7377' }}><Icon name={icon} size={20} /></div>
      <div style={{
        fontSize: 24, fontWeight: 800, lineHeight: 1,
        background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</div>
      {sub && <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Sparkbars({ series }) {
  if (!series?.length) return null;
  const max = Math.max(...series.map((s) => s.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '80px' }}>
      {series.map((s) => {
        const h = (s.count / max) * 100;
        return (
          <div key={s.day} title={`${s.day} · ${s.count} queries`} style={{
            flex: 1,
            height: `${h}%`,
            minHeight: 2,
            background: 'linear-gradient(180deg, #1d9c9c, #0d7377)',
            borderRadius: '3px 3px 0 0',
            transition: 'height 0.4s',
          }} />
        );
      })}
    </div>
  );
}

function ListRows({ items, render }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {items.map((r, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px',
          borderRadius: 8,
          background: 'rgba(255,255,255,0.6)',
          border: '1px solid rgba(29,156,156,0.08)',
          fontSize: 13,
        }}>
          <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 18 }}>{i + 1}</span>
          {render(r)}
        </div>
      ))}
    </div>
  );
}

function TimingTile({ label, value }) {
  return (
    <div style={{
      padding: '14px 12px',
      borderRadius: 10,
      background: 'rgba(255,255,255,0.85)',
      border: '1px solid rgba(29,156,156,0.12)',
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: 22, fontWeight: 800,
        background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>{value}</div>
      <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{text}</div>;
}
